/**
 * /calendar/lodging — the Lodging Calendar (Ted #19 → #939; 3b read,
 * 3c assign / move / undo + the same-family rule; absorbs #755).
 *
 * Ted, 09-09: *"This calendar is specifically tied to bed assignments … Each
 * numbered space represents a bed … Color coding so we can immediately see
 * when a reservation begins and when it ends … a dog checking out that day
 * should count as present/going home, but NOT toward that night's overnight
 * occupancy … Viewed by day, week, or month and navigated forward and
 * backward … with the actual dog counts kept separate from the number of
 * occupied beds."* And the thing he found misleading in Gingr: a dog with
 * no bed disappears from the calendar. Ours never does — the unassigned
 * boarders sit beside the grid, and (3c) get a bed from there.
 *
 * Ted's regulatory rule (3c): *"dogs from different families cannot actually
 * be housed together unsupervised … the system should never suggest that
 * two unrelated dogs can share a sleeping space simply to increase
 * capacity."* The server refuses an unrelated pairing (409, the dog named);
 * this page never OFFERS one — `lodging-moves.ts` judges every bed the desk
 * could pick or drop onto against the stays it can see. To see enough, the
 * read is widened past the visible range by DECISION_HORIZON_DAYS (the grid
 * still draws only the view's columns; the unassigned panel stays scoped
 * to the view). A stay longer than the horizon is the server's call, and
 * its refusal still reads as a sentence naming the dog.
 *
 * One read — `GET /api/v2/admin/calendar/lodging?start&end` — folded on the
 * server (`lodging-calendar.ts`) from the same `day-semantics` rule the
 * dashboard rollup uses, so the header's "occupied tonight" and the
 * dashboard's OVERNIGHT TONIGHT can never disagree about a checkout-day
 * dog. Writes go to the calendar's assign endpoints (POST / PATCH / DELETE
 * `/calendar/assign`), the same transactional path (advisory lock + gist
 * constraint) the run-of-show calendar uses; every write is followed by a
 * re-read, so the page shows the server's truth, never its own guess.
 *
 * Undo = the reverse call (an assign is undone by a DELETE, a move by a
 * PATCH back, an unassign by a POST back) — no client-side state replay.
 * Drag on the grid moves a stay to another BED; its nights follow its
 * booking (a night change is a booking-dates edit, not a bed edit — a bed
 * row outside the booking's nights would make this calendar and the
 * rollup disagree about that night).
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: one row per bed; beds occupied and dogs
 *    overnight are two labelled numbers, never blended; the threshold is
 *    the owner's number with its provenance.
 *  - #2 The next action is obvious: prev / next / today, three views; a
 *    bed-less inventory says where to add beds; an unassigned dog has an
 *    "Assign to bed…" picker; the last change has Undo beside it.
 *  - #5 Never lie about data: a stay that starts before or continues past
 *    the range says so; a retired bed still holding a stay stays on the
 *    grid; "Not set" for the threshold is named, never a zero; a refusal
 *    names the dog and the page re-reads.
 *  - #6 Tokens only: structural `--ferro-*` for status and the tone; the
 *    block wears the owner's `--service-*` color (#921).
 *  - #7 Built for hands: at 375 the week and month collapse to the day's
 *    bed list with day-by-day navigation; the bed pickers are native
 *    selects; every control ≥ 44 px.
 *
 * Counter Test: at 6:45 the desk opens Lodging, sees "8 of 15 beds tonight ·
 * 11 dogs", which bed each dog is in, who leaves today, and the two dogs
 * nobody has assigned yet — and puts them in a bed from the same screen.
 */
import { useCallback, useEffect, useMemo, useState, type DragEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { serviceColorClass } from '@ferro/core/modules/tenant-settings/service-colors.js';
import {
  readSession,
  todayIsoDate,
  adminLodgingCalendarApi,
  adminLodgingAssignApi,
  type LodgingCalendarView as LodgingData,
  type LodgingStay,
  type LodgingUnit,
  type LodgingUnassignedBoarder,
} from '../lib/api.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useBookingRefresh } from '../contexts/BookingRefreshContext.js';
import { useDashboardResource } from '../components/dashboard/useDashboardResource.js';
// Ted #28 Phase 2 (#1050) — the bed block carries the stay's flags from the ONE renderer.
import { DogFlagChips } from '../components/dog-first/index.js';
import { OwnerLink } from '../components/owner-link/index.js';
import {
  LODGING_VIEWS,
  formatDayHead,
  formatRangeLabel,
  formatSpan,
  isoDays,
  parseLodgingView,
  plural,
  rangeFor,
  shiftIsoDate,
  stepAnchor,
  type LodgingView,
} from '../lib/lodging-range.js';
import {
  bedOptionLabel,
  bedVerdict,
  describeChange,
  bedPickerState,
  placementOfBoarder,
  placementOfStay,
  reverseOf,
  type LodgingChange,
  type Placement,
} from '../lib/lodging-moves.js';
import { isIsoDate } from '../components/dashboard/day-list-filters.js';
import '../service-colors.css';
import './lodging-calendar.css';

type LodgingCalendarProps = {
  config: ClientConfig;
};

/** Below this width the week / month grids collapse to the day list. */
const PHONE_MAX = 640;

/**
 * How far past the visible range the read reaches so the same-family rule
 * can be judged for a stay that runs past the view (a stay longer than
 * this is the server's call). Month (≤ 31) + 31 stays under the route's
 * 92-day cap.
 */
const DECISION_HORIZON_DAYS = 31;

function usePhone(): boolean {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX);
  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return undefined;
    const mq = window.matchMedia(`(max-width: ${PHONE_MAX}px)`);
    const onChange = () => setPhone(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return phone;
}

const TONE_WORD: Record<'green' | 'amber' | 'red', string | null> = {
  green: null,
  amber: 'Approaching capacity',
  red: 'At capacity',
};

type Notice = { tone: 'error' | 'info'; text: string };

/** What the lists and the grid can ask the page to do (3c). */
interface LodgingActions {
  busy: boolean;
  /** Put an unassigned boarder, or a stay, on `unit` for its own nights. */
  placeOn: (p: Placement, dogNames: string[], unit: LodgingUnit) => void;
  /** Take a stay off its bed (undone by a POST back). */
  unassign: (stay: LodgingStay) => void;
}

export function LodgingCalendar({ config }: LodgingCalendarProps): JSX.Element {
  const { token } = readSession();
  const { isValidatingSession } = useAuth();
  const apiUrl = config.deployment.apiUrl;
  const [searchParams, setSearchParams] = useSearchParams();
  const phone = usePhone();

  const rawDate = searchParams.get('date');
  const anchor = isIsoDate(rawDate) ? rawDate : todayIsoDate();
  const requestedView = parseLodgingView(searchParams.get('view'));
  // Phone: the week and the month collapse to the day's bed list (Principle 7).
  const view: LodgingView = phone ? 'day' : requestedView;
  const { start, end } = rangeFor(view, anchor);
  // The read reaches past the view so the bed rule sees the stays it needs.
  const readEnd = shiftIsoDate(end, DECISION_HORIZON_DAYS);

  const { version: bookingRefreshVersion } = useBookingRefresh();
  const fetchesEnabled = Boolean(token) && !isValidatingSession;
  // Re-read when the range changes or the shell's Quick Book lands a booking
  // (the hook keys on this number; the range itself lives in the closure —
  // the same shape CalendarDay uses).
  const refreshKey = bookingRefreshVersion + hashRange(start, readEnd);
  const data = useDashboardResource<LodgingData>(
    () => adminLodgingCalendarApi.load(apiUrl, start, readEnd),
    refreshKey,
    fetchesEnabled,
  );

  // ── 3c: the last change (Undo issues its reverse) and any refusal ──
  const [lastChange, setLastChange] = useState<LodgingChange | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);

  const unitName = useCallback(
    (id: string) => data.data?.units.find((u) => u.id === id)?.name ?? 'a bed',
    [data.data],
  );

  const placeOn = useCallback(
    async (p: Placement, dogNames: string[], unit: LodgingUnit) => {
      if (busy) return;
      setBusy(true);
      setNotice(null);
      const span = { startDate: p.startDate, endDate: p.endDate };
      const prior = p.assignmentId ? (data.data?.stays.find((s) => s.assignmentId === p.assignmentId) ?? null) : null;
      const res = p.assignmentId
        ? await adminLodgingAssignApi.move(apiUrl, { replaceAssignmentId: p.assignmentId, bookingId: p.bookingId, unitId: unit.id, ...span })
        : await adminLodgingAssignApi.assign(apiUrl, { bookingId: p.bookingId, unitId: unit.id, ...span });
      setBusy(false);
      if (res.error || !res.data) {
        // The server's sentence names the dog; re-read so the view is the truth.
        setNotice({ tone: 'error', text: res.error ?? 'Couldn’t save that change.' });
        data.retry();
        return;
      }
      const id = res.data.assignment.id;
      setLastChange(
        prior
          ? {
              kind: 'moved',
              assignmentId: id,
              bookingId: p.bookingId,
              dogNames,
              from: { unitId: prior.resourceUnitId, unitName: unitName(prior.resourceUnitId), startDate: prior.startDate, endDate: prior.endDate },
              to: { unitId: unit.id, unitName: unit.name, ...span },
            }
          : { kind: 'assigned', assignmentId: id, bookingId: p.bookingId, dogNames, unitId: unit.id, unitName: unit.name, ...span },
      );
      data.retry();
    },
    [apiUrl, busy, data, unitName],
  );

  const unassign = useCallback(
    async (stay: LodgingStay) => {
      if (busy) return;
      setBusy(true);
      setNotice(null);
      const res = await adminLodgingAssignApi.unassign(apiUrl, stay.assignmentId);
      setBusy(false);
      if (res.error) {
        setNotice({ tone: 'error', text: res.error });
        data.retry();
        return;
      }
      setLastChange({
        kind: 'unassigned',
        bookingId: stay.bookingId,
        dogNames: stay.dogNames,
        from: { unitId: stay.resourceUnitId, unitName: unitName(stay.resourceUnitId), startDate: stay.startDate, endDate: stay.endDate },
      });
      data.retry();
    },
    [apiUrl, busy, data, unitName],
  );

  const undo = useCallback(async () => {
    if (!lastChange || busy) return;
    const rev = reverseOf(lastChange);
    setBusy(true);
    setNotice(null);
    const res =
      rev.method === 'DELETE'
        ? await adminLodgingAssignApi.unassign(apiUrl, rev.assignmentId)
        : rev.method === 'PATCH'
          ? await adminLodgingAssignApi.move(apiUrl, { replaceAssignmentId: rev.replaceAssignmentId, bookingId: rev.bookingId, unitId: rev.unitId, startDate: rev.startDate, endDate: rev.endDate })
          : await adminLodgingAssignApi.assign(apiUrl, { bookingId: rev.bookingId, unitId: rev.unitId, startDate: rev.startDate, endDate: rev.endDate });
    setBusy(false);
    if (res.error) {
      setNotice({ tone: 'error', text: `Couldn’t undo — ${res.error}` });
      data.retry();
      return;
    }
    setLastChange(null);
    setNotice({ tone: 'info', text: 'Undone.' });
    data.retry();
  }, [apiUrl, busy, data, lastChange]);

  const actions: LodgingActions = useMemo(() => ({ busy, placeOn, unassign }), [busy, placeOn, unassign]);

  const goTo = useCallback(
    (iso: string, nextView: LodgingView = requestedView) => {
      const params = new URLSearchParams();
      params.set('date', iso);
      if (nextView !== 'day') params.set('view', nextView);
      setSearchParams(params);
    },
    [requestedView, setSearchParams],
  );

  if (!token) {
    return (
      <section className="ferro-admin-page">
        <h1 className="ferro-section__title">Lodging</h1>
        <p className="ferro-muted">Please sign in to continue.</p>
      </section>
    );
  }

  const today = todayIsoDate();
  const isToday = anchor === today;
  const days = isoDays(start, end);
  const tonight = data.data?.nights.find((n) => n.date === anchor) ?? data.data?.nights[0] ?? null;

  return (
    <section className="ferro-admin-page ferro-lodge" data-testid="lodging-calendar" data-view={view}>
      <header className="ferro-lodge__header">
        <div className="ferro-lodge__nav">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-lodge__nav-btn"
            onClick={() => goTo(stepAnchor(view, anchor, -1))}
            aria-label={`Previous ${view}`}
            data-testid="lodging-prev"
          >
            ‹
          </button>
          <div className="ferro-lodge__date">
            <h1 className="ferro-lodge__title" data-testid="lodging-title">
              {formatRangeLabel(view, start, end)}
              {view === 'day' && isToday ? <span className="ferro-lodge__today"> · Today</span> : null}
            </h1>
            <label className="ferro-lodge__picker">
              <span className="ferro-lodge__sr">Pick a date</span>
              <input
                type="date"
                value={anchor}
                onChange={(e) => {
                  if (isIsoDate(e.target.value)) goTo(e.target.value);
                }}
                data-testid="lodging-date-input"
              />
            </label>
          </div>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-lodge__nav-btn"
            onClick={() => goTo(stepAnchor(view, anchor, 1))}
            aria-label={`Next ${view}`}
            data-testid="lodging-next"
          >
            ›
          </button>
        </div>
        <div className="ferro-lodge__tools">
          {!phone ? (
            <div className="ferro-lodge__views" role="group" aria-label="View">
              {LODGING_VIEWS.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className={`ferro-btn ferro-btn--sm ${requestedView === v.key ? 'ferro-btn--primary' : 'ferro-btn--ghost'}`}
                  aria-pressed={requestedView === v.key}
                  onClick={() => goTo(anchor, v.key)}
                  data-testid={`lodging-view-${v.key}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          ) : (
            <span className="ferro-muted ferro-lodge__phone-note" data-testid="lodging-phone-note">
              Day by day on a phone
            </span>
          )}
          {!isToday ? (
            <button type="button" className="ferro-btn ferro-btn--ghost" onClick={() => goTo(today)} data-testid="lodging-today">
              Back to today
            </button>
          ) : null}
        </div>
      </header>

      {/* ── Loading / error ──────────────────────────────────────────── */}
      {!data.data && data.isLoading ? (
        <div className="ferro-lodge__skeleton" data-testid="lodging-skeleton" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="ferro-lodge__skeleton-row" />
          ))}
        </div>
      ) : null}
      {!data.data && data.error ? (
        <div className="ferro-lodge__error" role="alert" data-testid="lodging-error">
          <span>Couldn’t load the lodging calendar.</span>
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={data.retry}>
            Retry
          </button>
        </div>
      ) : null}

      {/* ── 3c: a refusal names the dog; the last change carries Undo ── */}
      {notice ? (
        <div
          className={`ferro-lodge__notice ferro-lodge__notice--${notice.tone}`}
          role={notice.tone === 'error' ? 'alert' : 'status'}
          data-testid="lodging-notice"
          data-tone={notice.tone}
        >
          <span className="ferro-lodge__notice-text">{notice.text}</span>
          <button type="button" className="ferro-lodge__notice-close" onClick={() => setNotice(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      ) : null}
      {lastChange ? (
        <div className="ferro-lodge__change" data-testid="lodging-last-change">
          <span className="ferro-lodge__change-label">Last change</span>
          <span className="ferro-lodge__change-text" data-testid="lodging-last-change-text">
            {describeChange(lastChange)}
          </span>
          <button type="button" className="ferro-btn ferro-btn--sm ferro-btn--ghost" onClick={undo} disabled={busy} data-testid="lodging-undo">
            Undo
          </button>
        </div>
      ) : null}

      {data.data ? (
        <>
          {/* ── Tonight, in two numbers (never blended) + the threshold ── */}
          <OccupancyStrip data={data.data} anchor={anchor} view={view} tonight={tonight} />

          <div className="ferro-lodge__body">
            {data.data.units.length === 0 ? (
              <div className="ferro-lodge__empty" data-testid="lodging-empty">
                <p className="ferro-lodge__empty-title">No beds yet</p>
                <p className="ferro-muted">
                  Add each bed or cubicle in Settings → Lodging and this calendar gets one row per bed.
                </p>
                <Link to="/settings/lodging" className="ferro-btn ferro-btn--primary" data-testid="lodging-empty-link">
                  Set up beds →
                </Link>
              </div>
            ) : view === 'day' ? (
              <DayList data={data.data} date={anchor} actions={actions} />
            ) : (
              <Grid data={data.data} days={days} today={today} actions={actions} />
            )}

            <UnassignedPanel data={data.data} start={start} end={end} actions={actions} />
          </div>
        </>
      ) : null}
    </section>
  );
}

// ── The header numbers ────────────────────────────────────────────────────

function OccupancyStrip({
  data,
  anchor,
  view,
  tonight,
}: {
  data: LodgingData;
  anchor: string;
  view: LodgingView;
  tonight: LodgingData['nights'][number] | null;
}): JSX.Element {
  const beds = data.units.filter((u) => u.isActive).length;
  const threshold = data.threshold;
  const tone = tonight?.tone ?? 'green';
  const word = TONE_WORD[tone];
  const label = view === 'day' ? 'Tonight' : `Night of ${formatDayHead(anchor).weekday} ${formatDayHead(anchor).day}`;
  return (
    <div className={`ferro-lodge__strip ferro-lodge__strip--${tone}`} data-testid="lodging-strip" data-tone={tone}>
      <div className="ferro-lodge__stat">
        <span className="ferro-lodge__stat-label">{label} · beds occupied</span>
        <span className="ferro-lodge__stat-value" data-testid="lodging-beds-occupied">
          {tonight?.bedsOccupied ?? 0}
          <span className="ferro-lodge__stat-denom">
            {' '}
            {threshold !== null ? `of ${threshold}` : `of ${plural(beds, 'bed', 'beds')}`}
          </span>
        </span>
        <span className="ferro-lodge__stat-note" data-testid="lodging-threshold-note">
          {threshold !== null
            ? `${plural(beds, 'bed', 'beds')} in service · threshold ${threshold}${data.thresholdSource === 'default' ? ' (default)' : ''}`
            : 'No threshold set — showing the bed count. Set one in Settings → Lodging.'}
        </span>
        {word ? (
          <span className="ferro-lodge__word" data-testid="lodging-tone-word">
            {word}
          </span>
        ) : null}
      </div>
      <div className="ferro-lodge__stat">
        <span className="ferro-lodge__stat-label">Dogs overnight</span>
        <span className="ferro-lodge__stat-value" data-testid="lodging-dogs-overnight">
          {tonight?.dogsOvernight ?? 0}
        </span>
        <span className="ferro-lodge__stat-note">counted separately from beds</span>
      </div>
      <div className="ferro-lodge__stat">
        <span className="ferro-lodge__stat-label">Going home {view === 'day' ? 'today' : 'that day'}</span>
        <span className="ferro-lodge__stat-value" data-testid="lodging-going-home">
          {tonight?.goingHome ?? 0}
        </span>
        <span className="ferro-lodge__stat-note">present, not overnight</span>
      </div>
      <div className="ferro-lodge__stat">
        <span className="ferro-lodge__stat-label">Arriving</span>
        <span className="ferro-lodge__stat-value" data-testid="lodging-arriving">
          {tonight?.arriving ?? 0}
        </span>
        <span className="ferro-lodge__stat-note">first night</span>
      </div>
    </div>
  );
}

// ── The bed picker: only the beds the rule would accept are listed ────────

function BedPicker({
  id,
  label,
  placement,
  dogNames,
  data,
  actions,
  excludeUnitId,
}: {
  id: string;
  label: string;
  placement: Placement;
  dogNames: string[];
  data: LodgingData;
  actions: LodgingActions;
  excludeUnitId?: string;
}): JSX.Element {
  // Ted #35: zero beds is "set beds up", never "none free" — the threshold is not the inventory.
  const picker = bedPickerState(data.units, placement, data.stays, excludeUnitId);
  if (picker.kind === 'no-beds') {
    return (
      <span className="ferro-lodge__nobed" data-testid={`lodging-nobed-${id}`}>
        No beds set up yet — add them in Settings → Lodging.{' '}
        <Link to="/settings/lodging" className="ferro-lodge__nobed-link" data-testid={`lodging-nobed-setup-${id}`}>
          Set up beds →
        </Link>
      </span>
    );
  }
  if (picker.kind === 'none-free') {
    return (
      <span className="ferro-lodge__nobed" data-testid={`lodging-nobed-${id}`}>
        {picker.moving ? 'No other bed is free for these nights.' : 'No bed is free for these nights.'}
      </span>
    );
  }
  const beds = picker.beds;
  return (
    <label className="ferro-lodge__pick">
      <span className="ferro-lodge__sr">{label}</span>
      <select
        value=""
        disabled={actions.busy}
        onChange={(e) => {
          const unit = beds.find((u) => u.id === e.target.value);
          if (unit) actions.placeOn(placement, dogNames, unit);
        }}
        data-testid={`lodging-pick-${id}`}
      >
        <option value="">{label}</option>
        {beds.map((u) => (
          <option key={u.id} value={u.id}>
            {/* Sawyer 06 beds-lockers-runcard (B2) */}
            {bedOptionLabel(u)}
          </option>
        ))}
      </select>
    </label>
  );
}

// ── Day view: one row per bed, the stay on it tonight, who leaves today ───

function stayLabel(stay: LodgingStay): string {
  return stay.dogNames.length > 0 ? stay.dogNames.join(' & ') : 'Unnamed dog';
}

function DayList({ data, date, actions }: { data: LodgingData; date: string; actions: LodgingActions }): JSX.Element {
  const byUnit = new Map<string, { tonight: LodgingStay | null; leaving: LodgingStay[] }>();
  for (const u of data.units) byUnit.set(u.id, { tonight: null, leaving: [] });
  for (const s of data.stays) {
    const slot = byUnit.get(s.resourceUnitId);
    if (!slot) continue;
    if (s.nightsInRange.includes(date)) slot.tonight = s;
    else if (s.checkoutDate === date) slot.leaving.push(s);
  }
  let lastZone = '';
  return (
    <ul className="ferro-lodge__list" data-testid="lodging-day-list">
      {data.units.map((u) => {
        const slot = byUnit.get(u.id)!;
        const zoneHead = u.zone !== lastZone ? u.zone : null;
        lastZone = u.zone;
        const stay = slot.tonight;
        return (
          <li key={u.id} className="ferro-lodge__item" data-testid={`lodging-bed-${u.id}`}>
            {zoneHead ? <div className="ferro-lodge__zone">{zoneHead}</div> : null}
            <div className="ferro-lodge__item-row">
              <div className="ferro-lodge__bed">
                <span className="ferro-lodge__bed-name">{u.name}</span>
                <span className="ferro-lodge__bed-meta">
                  {u.kind.charAt(0).toUpperCase() + u.kind.slice(1)}
                  {/* Sawyer 06 beds-lockers-runcard (B2) */}
                  {u.sizeClass ? <span data-testid={`lodging-bed-size-${u.id}`}>{` · ${u.sizeClass}`}</span> : null}
                  {u.capacity > 1 ? ` · up to ${u.capacity} dogs` : ''}
                  {!u.isActive ? ' · Retired' : ''}
                </span>
              </div>
              {stay ? (
                <StayBlock stay={stay} rangeStart={date} rangeEnd={date} testId={`lodging-stay-${stay.assignmentId}`} />
              ) : (
                <span className="ferro-lodge__free" data-testid={`lodging-free-${u.id}`}>
                  Free tonight
                </span>
              )}
            </div>
            {stay ? (
              <div className="ferro-lodge__item-actions">
                <BedPicker
                  id={stay.assignmentId}
                  label="Move to…"
                  placement={placementOfStay(stay)}
                  dogNames={stay.dogNames}
                  data={data}
                  actions={actions}
                  excludeUnitId={u.id}
                />
                <button
                  type="button"
                  className="ferro-btn ferro-btn--sm ferro-btn--ghost"
                  disabled={actions.busy}
                  onClick={() => actions.unassign(stay)}
                  data-testid={`lodging-unassign-${stay.assignmentId}`}
                >
                  Take off bed
                </button>
              </div>
            ) : null}
            {slot.leaving.map((s) => (
              <div key={s.assignmentId} className="ferro-lodge__leaving" data-testid={`lodging-leaving-${s.assignmentId}`}>
                <span className={['ferro-service-chip', serviceColorClass(s.serviceColor)].filter(Boolean).join(' ')}>{stayLabel(s)}</span>
                <span className="ferro-status-pill ferro-status-pill--success">Going home today</span>
                <span className="ferro-muted">
                  <OwnerLink name={s.customerName} customerId={s.customerId} /> · {formatSpan(s.startDate, s.checkoutDate)} · not counted tonight
                </span>
              </div>
            ))}
          </li>
        );
      })}
    </ul>
  );
}

// ── Week / month grid ─────────────────────────────────────────────────────

function Grid({ data, days, today, actions }: { data: LodgingData; days: string[]; today: string; actions: LodgingActions }): JSX.Element {
  const nightByDate = new Map(data.nights.map((n) => [n.date, n]));
  const staysByUnit = new Map<string, LodgingStay[]>();
  for (const s of data.stays) {
    const list = staysByUnit.get(s.resourceUnitId) ?? [];
    list.push(s);
    staysByUnit.set(s.resourceUnitId, list);
  }
  const firstDay = days[0] ?? '';
  const lastDay = days[days.length - 1] ?? '';
  // 3c — the stay being dragged; each bed row says whether it would take it.
  const [dragging, setDragging] = useState<LodgingStay | null>(null);
  const dropVerdict = (u: LodgingUnit): 'ok' | 'no' | null => {
    if (!dragging) return null;
    if (u.id === dragging.resourceUnitId) return null;
    return bedVerdict(u, placementOfStay(dragging), data.stays).ok ? 'ok' : 'no';
  };
  let lastZone = '';
  return (
    <div className="ferro-lodge__grid-wrap">
      <table className="ferro-lodge__grid" data-testid="lodging-grid">
        <thead>
          <tr>
            <th className="ferro-lodge__corner" scope="col">
              Bed
            </th>
            {days.map((d) => {
              const head = formatDayHead(d);
              const night = nightByDate.get(d);
              return (
                <th key={d} scope="col" className={`ferro-lodge__day${d === today ? ' ferro-lodge__day--today' : ''}`} data-testid={`lodging-col-${d}`}>
                  <span className="ferro-lodge__day-name">{head.weekday}</span>
                  <span className="ferro-lodge__day-num">{head.day}</span>
                  {night ? (
                    <span className={`ferro-lodge__day-count ferro-lodge__day-count--${night.tone}`} data-testid={`lodging-night-${d}`} data-tone={night.tone}>
                      {night.bedsOccupied}
                      <span className="ferro-lodge__day-count-sep">/</span>
                      {night.dogsOvernight}
                    </span>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.units.map((u) => {
            const zoneHead = u.zone !== lastZone ? u.zone : null;
            lastZone = u.zone;
            const stays = staysByUnit.get(u.id) ?? [];
            const verdict = dropVerdict(u);
            const cellDropClass = verdict === 'ok' ? ' ferro-lodge__cell--drop-ok' : verdict === 'no' ? ' ferro-lodge__cell--drop-no' : '';
            const onDragOver = (e: DragEvent<HTMLTableCellElement>) => {
              if (verdict === 'ok') {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }
            };
            const onDrop = (e: DragEvent<HTMLTableCellElement>) => {
              e.preventDefault();
              if (dragging && verdict === 'ok') actions.placeOn(placementOfStay(dragging), dragging.dogNames, u);
              setDragging(null);
            };
            return (
              <>
                {zoneHead ? (
                  <tr key={`zone-${u.id}`} className="ferro-lodge__zone-row">
                    <th scope="rowgroup" colSpan={days.length + 1}>
                      {zoneHead}
                    </th>
                  </tr>
                ) : null}
                <tr key={u.id} data-testid={`lodging-row-${u.id}`} data-drop={verdict ?? undefined}>
                  <th scope="row" className="ferro-lodge__bed-cell">
                    <span className="ferro-lodge__bed-name">{u.name}</span>
                    <span className="ferro-lodge__bed-meta">
                      {/* Sawyer 06 beds-lockers-runcard (B2) */}
                      {u.sizeClass ? <span data-testid={`lodging-row-size-${u.id}`}>{u.sizeClass}{u.capacity > 1 ? ' · ' : ''}</span> : null}
                      {u.capacity > 1 ? `up to ${u.capacity} dogs` : ''}
                      {!u.isActive ? 'Retired' : ''}
                    </span>
                  </th>
                  {days.map((d) => {
                    const stay = stays.find((s) => s.nightsInRange.includes(d)) ?? null;
                    const leaving = stays.find((s) => s.checkoutDate === d && !s.nightsInRange.includes(d)) ?? null;
                    const cellClass = `ferro-lodge__cell${d === today ? ' ferro-lodge__cell--today' : ''}${cellDropClass}`;
                    if (!stay) {
                      return (
                        <td key={d} className={cellClass} onDragOver={onDragOver} onDrop={onDrop} data-testid={`lodging-cell-${u.id}-${d}`}>
                          {leaving ? (
                            <span className="ferro-lodge__out" data-testid={`lodging-out-${leaving.assignmentId}-${d}`} title={`${stayLabel(leaving)} goes home`}>
                              ↤
                            </span>
                          ) : null}
                        </td>
                      );
                    }
                    // The read reaches past the columns (the decision horizon):
                    // clip the segment to what is on screen and say "…" where
                    // the stay runs past the last column.
                    const visible = stay.nightsInRange.filter((n) => n >= firstDay && n <= lastDay);
                    const first = visible[0] === d;
                    const last = visible[visible.length - 1] === d;
                    const finalNight = stay.nightsInRange[stay.nightsInRange.length - 1] ?? '';
                    const runsPast = stay.continuesAfter || finalNight > lastDay;
                    const cls = [
                      'ferro-lodge__seg',
                      serviceColorClass(stay.serviceColor),
                      first ? 'ferro-lodge__seg--first' : '',
                      last ? 'ferro-lodge__seg--last' : '',
                      first && stay.continuesFromBefore ? 'ferro-lodge__seg--from-before' : '',
                      last && runsPast ? 'ferro-lodge__seg--continues' : '',
                      stay.shared ? 'ferro-lodge__seg--shared' : '',
                      first ? 'ferro-lodge__seg--draggable' : '',
                      dragging?.assignmentId === stay.assignmentId ? 'ferro-lodge__seg--dragging' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <td key={d} className={cellClass} onDragOver={onDragOver} onDrop={onDrop} data-testid={`lodging-cell-${u.id}-${d}`}>
                        <div
                          className={cls}
                          data-testid={first ? `lodging-stay-${stay.assignmentId}` : undefined}
                          title={`${stayLabel(stay)} · ${stay.customerName} · ${formatSpan(stay.startDate, stay.checkoutDate)}${first ? ' · drag to another bed' : ''}`}
                          draggable={first && !actions.busy}
                          onDragStart={
                            first
                              ? (e) => {
                                  e.dataTransfer.setData('text/plain', stay.assignmentId);
                                  e.dataTransfer.effectAllowed = 'move';
                                  setDragging(stay);
                                }
                              : undefined
                          }
                          onDragEnd={first ? () => setDragging(null) : undefined}
                        >
                          {first ? (
                            <>
                              <span className="ferro-lodge__seg-label">
                                {stay.continuesFromBefore ? '… ' : ''}
                                {stayLabel(stay)}
                              </span>
                              {stay.shared ? (
                                <span className="ferro-lodge__seg-family" data-testid={`lodging-family-${stay.assignmentId}`}>
                                  {plural(stay.dogCount, 'dog', 'dogs')} · {stay.customerName}
                                </span>
                              ) : null}
                            </>
                          ) : null}
                          {last && runsPast ? <span className="ferro-lodge__seg-more">…</span> : null}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StayBlock({ stay, rangeStart, rangeEnd, testId }: { stay: LodgingStay; rangeStart: string; rangeEnd: string; testId: string }): JSX.Element {
  void rangeStart;
  void rangeEnd;
  const nightIndex = Math.max(1, Math.round((new Date(`${stay.nightsInRange[0] ?? stay.startDate}T00:00:00Z`).getTime() - new Date(`${stay.startDate}T00:00:00Z`).getTime()) / 86_400_000) + 1);
  return (
    <div className={['ferro-lodge__block', serviceColorClass(stay.serviceColor), stay.shared ? 'ferro-lodge__block--shared' : ''].filter(Boolean).join(' ')} data-testid={testId}>
      <span className="ferro-lodge__block-dogs">
        {stayLabel(stay)}
        {/* Ted #28 Phase 2 — INTACT / vax / meds / transport on the block
            (the union over the family; which dog is one tap away). */}
        <span className="ferro-lodge__block-flags">
          <DogFlagChips flags={stay.flags} scope="dog" testIdPrefix={`lodging-${stay.bookingId}-`} />
        </span>
      </span>
      <span className="ferro-lodge__block-meta">
        {stay.serviceDisplayName} · night {nightIndex} of {stay.nights} · {formatSpan(stay.startDate, stay.checkoutDate)}
      </span>
      <span className="ferro-lodge__block-owner">
        {stay.shared ? `${plural(stay.dogCount, 'dog', 'dogs')} from one family · ` : ''}
        <OwnerLink name={stay.customerName} customerId={stay.customerId} />
        {stay.status === 'confirmed' || stay.status === 'pending' ? ' · arriving' : ''}
      </span>
    </div>
  );
}

// ── The dogs nobody assigned — never hidden; a bed is one pick away ───────

function UnassignedPanel({ data, start, end, actions }: { data: LodgingData; start: string; end: string; actions: LodgingActions }): JSX.Element {
  // The read reaches past the view (the decision horizon); the panel stays
  // scoped to the boarders present in the view's own range (inclusive dates).
  const listed: LodgingUnassignedBoarder[] = data.unassigned.filter((b) => b.startDate <= end && b.endDate >= start);
  return (
    <aside className="ferro-lodge__unassigned" aria-label="Not yet assigned to a bed" data-testid="lodging-unassigned">
      <h2 className="ferro-lodge__unassigned-title">
        Not yet assigned to a bed
        <span className="ferro-lodge__unassigned-count" data-testid="lodging-unassigned-count">
          {listed.length}
        </span>
      </h2>
      {listed.length === 0 ? (
        <p className="ferro-muted" data-testid="lodging-unassigned-empty">
          Every boarder in this range has a bed.
        </p>
      ) : (
        <ul className="ferro-lodge__unassigned-list">
          {listed.map((b) => (
            <li key={b.bookingId} className="ferro-lodge__unassigned-item" data-testid={`lodging-unassigned-${b.bookingId}`}>
              <span className={['ferro-service-chip', serviceColorClass(b.serviceColor)].filter(Boolean).join(' ')}>
                {b.dogNames.join(' & ') || 'Unnamed dog'}
              </span>
              <span className="ferro-lodge__block-flags">
                <DogFlagChips flags={b.flags} scope="dog" testIdPrefix={`lodging-unassigned-${b.bookingId}-`} />
              </span>
              <span className="ferro-lodge__unassigned-meta">
                <OwnerLink name={b.customerName} customerId={b.customerId} /> · {formatSpan(b.startDate, b.endDate)}
                {b.dogCount > 1 ? ` · ${plural(b.dogCount, 'dog', 'dogs')}` : ''}
              </span>
              <BedPicker id={b.bookingId} label="Assign to bed…" placement={placementOfBoarder(b)} dogNames={b.dogNames} data={data} actions={actions} />
            </li>
          ))}
        </ul>
      )}
      <p className="ferro-muted ferro-lodge__unassigned-note">
        Still counted in tonight’s dogs. Only beds the family rule allows are offered.
      </p>
    </aside>
  );
}

/** A stable number for a range so the resource hook refetches when it moves. */
function hashRange(start: string, end: string): number {
  let h = 0;
  for (const ch of `${start}|${end}`) h = (h * 31 + ch.charCodeAt(0)) % 1_000_000_007;
  return h * 1000;
}
